// =====================================================================================
// INVRT Radar — web console — account — delivery — one endpoint's delivery history
//
// GET /webhooks/:id/deliveries (contract §7), newest first, in a modal "drawer" built on
// the house Dialog. Each row is the server's own delivery record: event, state, how many
// attempts, the last HTTP status, when. "Redeliver" (admin only) queues one more attempt
// at THAT row — any state, including dead — and the list is re-read rather than patched.
// The payload itself is not rendered here: it can be a full Contact, and a history table
// is not the place for a person's details.
// =====================================================================================

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/format";
import type { RedeliverWebhookResponse, WebhookDelivery, WebhookEndpoint } from "@/lib/types.c1";
import { DeliveryStateBadge } from "./badges";
import {
  DRAWER_PAGE_LIMIT,
  eventKindLabel,
  httpStatusText,
  useWebhookDeliveries,
  webhookDeliveriesKey,
} from "./hooks";

export interface DeliveriesDrawerProps {
  endpoint: WebhookEndpoint;
  isAdmin: boolean;
  onClose: () => void;
}

export function DeliveriesDrawer({ endpoint, isAdmin, onClose }: DeliveriesDrawerProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const deliveries = useWebhookDeliveries(endpoint.id);

  const redeliver = useMutation({
    mutationFn: (delivery: WebhookDelivery) =>
      api.post<RedeliverWebhookResponse>(
        `/webhooks/${endpoint.id}/deliveries/${delivery.id}/redeliver`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webhookDeliveriesKey(orgId, endpoint.id) });
      push({
        tone: "success",
        title: "Redelivery queued",
        body: "The event is sent again shortly, signed with the endpoint's current secret.",
      });
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Deliveries"
      description={endpoint.url}
      className="w-[min(60rem,calc(100vw-2rem))]"
    >
      {deliveries.isLoading ? <LoadingState label="Loading deliveries…" /> : null}
      {deliveries.isError ? (
        <ErrorState error={deliveries.error} onRetry={() => void deliveries.refetch()} />
      ) : null}
      {deliveries.isSuccess ? (
        deliveries.data.deliveries.length === 0 ? (
          <EmptyState
            title="No deliveries yet"
            body="Every event Radar sends to this endpoint is listed here with what the endpoint answered."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {redeliver.isError ? <ErrorState error={redeliver.error} /> : null}
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Event</TableHeaderCell>
                  <TableHeaderCell>State</TableHeaderCell>
                  <TableHeaderCell className="text-right">Attempts</TableHeaderCell>
                  <TableHeaderCell>Last status</TableHeaderCell>
                  <TableHeaderCell>Last attempt</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  {isAdmin ? <TableHeaderCell className="text-right">Actions</TableHeaderCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.data.deliveries.map((delivery) => (
                  <TableRow key={delivery.id}>
                    <TableCell>{eventKindLabel(delivery.event_kind)}</TableCell>
                    <TableCell>
                      <DeliveryStateBadge state={delivery.state} />
                    </TableCell>
                    <TableCell className="text-right font-mono">{delivery.attempts}</TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {httpStatusText(delivery.last_status_code)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(delivery.last_attempt_at)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(delivery.created_at)}
                    </TableCell>
                    {isAdmin ? (
                      <TableCell className="text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => redeliver.mutate(delivery)}
                          loading={redeliver.isPending && redeliver.variables?.id === delivery.id}
                        >
                          Redeliver
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {deliveries.data.has_more ? (
              <p className="text-xs text-ink-muted">
                Showing the most recent {DRAWER_PAGE_LIMIT} deliveries.
              </p>
            ) : null}
          </div>
        )
      ) : null}
    </Dialog>
  );
}
